import React from 'react';

/**
 * Error boundary that catches React rendering crashes.
 *
 * - Stale-cache errors (chunk load, TDZ) → delegate to nuke recovery
 * - Runtime errors (null ref, bad data) → show error UI with refresh button
 *   (nuking caches cannot fix runtime bugs and causes a death spiral)
 */
class ChunkLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isStaleCache: false };
  }

  static getDerivedStateFromError(error) {
    const msg = (error?.message || '') + ' ' + (error?.name || '');
    const isStaleCache = (
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk') ||
      msg.includes('dynamically imported module') ||
      msg.includes('before initialization') ||
      (error instanceof SyntaxError)
    );
    return { hasError: true, isStaleCache };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ChunkLoadErrorBoundary] Caught:', error?.name, error?.message);

    if (this.state.isStaleCache && typeof window.nukeAndReload === 'function') {
      window.nukeAndReload();
    }
    // For non-cache errors: render() shows error UI below
  }

  render() {
    if (this.state.hasError && !this.state.isStaleCache) {
      return (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          minHeight: '100vh', background: '#f9fafb', padding: '16px',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <p style={{ color: '#374151', fontSize: '16px', marginBottom: '8px' }}>
              Something went wrong.
            </p>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
              Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 32px', background: '#3b82f6', color: '#fff',
                border: 'none', borderRadius: '8px', fontSize: '16px',
                cursor: 'pointer', fontFamily: 'inherit', marginRight: '8px'
              }}
            >
              Refresh
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              style={{
                padding: '12px 32px', background: '#e5e7eb', color: '#374151',
                border: 'none', borderRadius: '8px', fontSize: '16px',
                cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }
    if (this.state.hasError) {
      // Stale-cache error — nukeAndReload handles the UI
      return null;
    }
    return this.props.children;
  }
}

export default ChunkLoadErrorBoundary;
